import { useEffect, useRef, useState, useCallback } from "react"

interface Props {
  isUnderModal?: boolean
  targetId?: string
}

const useFixedPosition = ({ isUnderModal = false, targetId }: Props) => {
  const baseRef = useRef<any>(null)
  const [top, setTop] = useState(0)
  const [left, setLeft] = useState(0)
  const [targetHeight, setTargetHeight] = useState(0)
  const targetElementRef = useRef<HTMLElement | null>(null)
  const lastHeightRef = useRef<number>(0)

  const updatePosition = useCallback(() => {
    const element = baseRef.current
    const targetElement = targetElementRef.current

    if (element && targetElement) {
      const rect = element.getBoundingClientRect()
      const currentHeight = targetElement.offsetHeight

      setTargetHeight(currentHeight)

      if (isUnderModal) {
        setTop(element.offsetTop - element.offsetTop / 2)
        setLeft(element.offsetLeft - element.offsetLeft / 2)
      } else {
        setTop(rect.top - currentHeight / 2)
        setLeft(rect.left)
      }
      lastHeightRef.current = currentHeight
    }
  }, [isUnderModal])

  useEffect(() => {
    const checkForElement = () => {
      const targetElement = document.getElementById(targetId ?? "")

      if (targetElement) {
        targetElementRef.current = targetElement
        lastHeightRef.current = targetElement.offsetHeight
        updatePosition()

        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.target === targetElement) {
              const currentHeight = targetElement.offsetHeight
              if (currentHeight !== lastHeightRef.current) {
                updatePosition()
              }
            }
          }
        })

        resizeObserver.observe(targetElement)

        const removalObserver = new MutationObserver((mutations) => {
          const elementRemoved = mutations.some(mutation =>
            Array.from(mutation.removedNodes).some(node =>
              node.nodeType === Node.ELEMENT_NODE &&
              (node as Element).id === targetId
            )
          )

          if (elementRemoved) {
            targetElementRef.current = null
            resizeObserver.disconnect()
            removalObserver.disconnect()
            setupMutationObserver()
          }
        })

        removalObserver.observe(document.body, {
          childList: true,
          subtree: true
        })

        return { resizeObserver, removalObserver }
      }
      return null
    }

    const setupMutationObserver = () => {
      const mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element
              if (element.id === targetId || element.querySelector(`#${targetId}`)) {
                mutationObserver.disconnect()
                const observers = checkForElement()
                if (observers) {
                  currentObservers = observers
                }
                return
              }
            }
          }
        }
      })

      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      })

      return mutationObserver
    }

    let currentObservers = checkForElement()

    if (!currentObservers) {
      const mutationObserver = setupMutationObserver()

      return () => {
        mutationObserver.disconnect()
        if (currentObservers) {
          currentObservers.resizeObserver?.disconnect()
          currentObservers.removalObserver?.disconnect()
        }
      }
    }

    return () => {
      if (currentObservers) {
        currentObservers.resizeObserver?.disconnect()
        currentObservers.removalObserver?.disconnect()
      }
    }
  }, [targetId, updatePosition])

  return { top, left, baseRef, targetHeight }
}

export default useFixedPosition
